import { HttpRequestConfig } from '@okapi/http';
import { ApiClient } from 'api/types/api-client';

export class FetchApiClient implements ApiClient {
  private readonly baseUrl: string;

  private readonly defaultHeaders: Record<string, string>;

  constructor(baseUrl: string, apiKey: string) {
    this.baseUrl = baseUrl;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      'X-API-Key': apiKey,
    };
  }

  private buildFullUrl(path: string, params?: unknown): string {
    const normalizedBase = this.baseUrl.endsWith('/') ? this.baseUrl : this.baseUrl + '/';
    const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
    const fullUrl = normalizedBase + normalizedPath;
    const url = new URL(fullUrl);

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          url.searchParams.append(key, String(value));
        }
      });
    }

    return url.toString();
  }

  private async _request<TResponse = unknown>(
    method: 'GET' | 'POST',
    path: string,
    config?: {
      params?: unknown;
      body?: unknown;
      options?: HttpRequestConfig;
    }
  ): Promise<TResponse> {
    const { params, body, options } = config || {};
    const url = this.buildFullUrl(path, params);

    const response = await fetch(url, {
      method,
      headers: {
        ...this.defaultHeaders,
        ...(options?.headers as Record<string, string>),
      },
      credentials: options?.withCredentials ? 'include' : 'same-origin',
      body: body ? JSON.stringify(body) : undefined,
      keepalive: method === 'POST' ? true : undefined,
    });

    if (!response.ok) {
      throw new Error(`ApiClient HTTP error with status: ${response.status}`);
    }

    return response.json() as Promise<TResponse>;
  }

  public async get<
    TResponse = unknown,
    TPath extends string = string,
    TParams = unknown,
    TOptions extends HttpRequestConfig = HttpRequestConfig
  >(path: TPath, params?: TParams, options?: TOptions): Promise<TResponse> {
    return this._request<TResponse>('GET', path, { params, options });
  }

  public async post<
    TResponse = unknown,
    TPath extends string = string,
    TBody = unknown,
    TOptions extends HttpRequestConfig = HttpRequestConfig
  >(path: TPath, body: TBody, options?: TOptions): Promise<TResponse> {
    return this._request<TResponse>('POST', path, { body, options });
  }
}
